
import { supabase } from '@/integrations/supabase/client';

export interface CompanyData {
  companyName: string;
  companySize: string;
}

interface CompanyResult {
  id: string;
  name: string;
  slug: string;
  active: boolean;
}

/**
 * Helper function to create a URL-safe slug from company name.
 * Exported for use in other parts of the app.
 */
export const createCompanySlug = (companyName: string): string => {
  if (!companyName || typeof companyName !== 'string') {
    return 'company-' + Date.now();
  }

  let slug = companyName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .substring(0, 50);

  if (!slug || slug.length === 0) {
    return 'company-' + Date.now();
  }

  return slug;
};

/**
 * Creates or retrieves a company for the authenticated user using a secure RPC function.
 * This approach bypasses RLS safely via SECURITY DEFINER while validating auth.uid().
 */
export const findOrCreateCompany = async (companyData: CompanyData): Promise<CompanyResult> => {
  const { companyName, companySize } = companyData;
  
  if (!companyName || !companyName.trim()) {
    console.error('findOrCreateCompany: Company name is required');
    throw new Error('Company name is required');
  }

  console.log('findOrCreateCompany: Using secure RPC for:', companyName);
  
  try {
    const { data, error } = await supabase.rpc('create_company_for_user', {
      p_company_name: companyName.trim(),
      p_company_size: companySize || 'small'
    });

    if (error) {
      console.error('findOrCreateCompany: RPC error:', error);
      throw error;
    }

    if (!data) {
      console.error('findOrCreateCompany: RPC returned no data');
      throw new Error('Failed to create company - no data returned');
    }

    // Cast the JSONB result to our expected type
    const result = data as unknown as CompanyResult;

    console.log('findOrCreateCompany: Success:', result);
    
    return {
      id: result.id,
      name: result.name,
      slug: result.slug,
      active: result.active
    };
  } catch (error) {
    console.error('findOrCreateCompany: Fatal error:', error);
    throw error;
  }
};
